
/**
 * Service de déchiffrement pour les données sensibles, compatible avec AES-256-GCM du backend.
 * Supporte les formats legacy (4 parties), v1 (5 parties) et v2 (4 parties avec clé globale).
 */
class DecryptionService {
  private static readonly KEY_LENGTH = 32; // 256 bits
  private static readonly PLACEHOLDER = 'Données protégées';
  private static readonly STATIC_SALT_V2 = 'ekose_hds_global_static_salt_v2';
  private static GLOBAL_KEY_V2: CryptoKey | null = null;

  private static looksEncrypted(text: string): boolean {
    if (!text || typeof text !== 'string') return false;
    if (!text.includes(':')) return false;
    const parts = text.split(':');
    // v2: 4 parts, v1: 5 parts, legacy: 4 parts
    if (parts.length !== 4 && parts.length !== 5) return false;
    
    // Check if it's a versioned format
    if (parts[0] === 'v1' || parts[0] === 'v2') return true;
    
    // Legacy check: all parts should be hex
    return parts.every((part) => /^[0-9a-f]+$/i.test(part));
  }

  private static hexToArrayBuffer(hex: string): ArrayBuffer {
    const bytes = new Uint8Array(hex.length / 2);
    for (let i = 0; i < hex.length; i += 2) {
      bytes[i / 2] = parseInt(hex.substring(i, i + 2), 16);
    }
    return bytes.buffer;
  }

  /**
   * Importe la clé maîtresse brute pour dérivation
   */
  private static async importMasterKey(): Promise<CryptoKey> {
    const keyHex = import.meta.env.VITE_ENCRYPTION_KEY;
    if (!keyHex || keyHex.length !== this.KEY_LENGTH * 2) {
      throw new Error(`VITE_ENCRYPTION_KEY invalide`);
    }
    const keyBuffer = this.hexToArrayBuffer(keyHex);
    return window.crypto.subtle.importKey(
      'raw', 
      keyBuffer, 
      { name: 'PBKDF2' }, 
      false, 
      ['deriveKey']
    );
  }

  /**
   * Dérive une clé AES-GCM via PBKDF2
   */
  private static async deriveKey(masterKey: CryptoKey, salt: ArrayBuffer): Promise<CryptoKey> {
    return window.crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt: salt,
        iterations: 100000,
        hash: 'SHA-512',
      },
      masterKey,
      { name: 'AES-GCM', length: 256 },
      true,
      ['decrypt']
    );
  }

  /**
   * Initialise ou récupère la clé globale V2
   */
  private static async getGlobalKeyV2(): Promise<CryptoKey> {
    if (this.GLOBAL_KEY_V2) return this.GLOBAL_KEY_V2;
    const masterKey = await this.importMasterKey();
    const staticSalt = new TextEncoder().encode(this.STATIC_SALT_V2);
    this.GLOBAL_KEY_V2 = await this.deriveKey(masterKey, staticSalt.buffer);
    return this.GLOBAL_KEY_V2;
  }

  /**
   * Déchiffre une chaîne de caractères selon sa version
   */
  static async decrypt(encryptedText: string): Promise<string> {
    if (typeof encryptedText !== 'string' || !this.looksEncrypted(encryptedText)) {
      return encryptedText;
    }

    const keyHex = import.meta.env.VITE_ENCRYPTION_KEY;
    if (!keyHex) return this.PLACEHOLDER;

    try {
      const parts = encryptedText.split(':');
      let iv: ArrayBuffer;
      let tag: ArrayBuffer;
      let encrypted: ArrayBuffer;
      let decryptionKey: CryptoKey;

      if (parts[0] === 'v2') {
        // Format v2: v2:iv:tag:encrypted
        const [, ivHex, tagHex, encryptedHex] = parts;
        iv = this.hexToArrayBuffer(ivHex);
        tag = this.hexToArrayBuffer(tagHex);
        encrypted = this.hexToArrayBuffer(encryptedHex);
        decryptionKey = await this.getGlobalKeyV2();
      } else {
        // Format v1 ou Legacy
        const isV1 = parts[0] === 'v1';
        const offset = isV1 ? 1 : 0;
        const saltHex = parts[offset];
        const ivHex = parts[offset + 1];
        const tagHex = parts[offset + 2];
        const encryptedHex = parts[offset + 3];

        iv = this.hexToArrayBuffer(ivHex);
        tag = this.hexToArrayBuffer(tagHex);
        encrypted = this.hexToArrayBuffer(encryptedHex);
        
        const salt = this.hexToArrayBuffer(saltHex);
        const masterKey = await this.importMasterKey();
        decryptionKey = await this.deriveKey(masterKey, salt);
      }

      // Concaténer les données chiffrées et le tag pour Web Crypto
      const combined = new Uint8Array(encrypted.byteLength + tag.byteLength);
      combined.set(new Uint8Array(encrypted), 0);
      combined.set(new Uint8Array(tag), encrypted.byteLength);

      const decryptedBuffer = await window.crypto.subtle.decrypt(
        { name: 'AES-GCM', iv },
        decryptionKey,
        combined.buffer
      );

      return new TextDecoder().decode(decryptedBuffer);
    } catch (error) {
      console.debug('⚠️ Échec déchiffrement:', error);
      return this.PLACEHOLDER;
    }
  }

  static async smartDecrypt(text: string): Promise<string> {
    if (!text || typeof text !== 'string') return text || '';
    if (!this.looksEncrypted(text)) return text;
    return await this.decrypt(text);
  }

  static decryptUserData(userData: Record<string, unknown>): Record<string, unknown> {
    const decrypted = { ...userData };
    const fieldsToDecrypt = ['name', 'email', 'phoneNumber', 'phone_number', 'residence', 'speciality', 'licenseNumber'];

    for (const field of fieldsToDecrypt) {
      if (typeof decrypted[field] === 'string') {
        // Note: Cette méthode est synchrone mais le déchiffrement est asynchrone.
        // Dans une UI React, il vaut mieux déchiffrer au niveau du composant ou via un hook.
        // Mais pour la compatibilité, on laisse le format tel quel si on ne peut pas await ici.
      }
    }
    return decrypted;
  }

  static async decryptObjectContent<T extends { content?: string }>(obj: T): Promise<T> {
    if (obj.content && typeof obj.content === 'string') {
      return { ...obj, content: await this.decrypt(obj.content) };
    }
    return obj;
  }

  static safeDecrypt(encryptedText: string | undefined | null, fallback: string = ''): string {
    if (!encryptedText || typeof encryptedText !== 'string') return fallback;
    if (!this.looksEncrypted(encryptedText)) return encryptedText;
    return this.PLACEHOLDER; // On ne peut pas déchiffrer de manière synchrone avec WebCrypto
  }
}

export default DecryptionService;
